// Deadline maths for a Bulk Shell run. Pure, with no `@/` imports, so
// `npm test` runs it under node --test (relative imports carry `.ts`).
import { offsetLabel } from './run-timeline.ts';
import { formatDateTime } from './timezone.ts';

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;
export const MIN_DEADLINE_MS = MINUTE;
export const MAX_DEADLINE_MS = 30 * 24 * 60 * MINUTE;

export type DeadlineChoice = 'none' | '1h' | '4h' | '24h' | '7d' | 'custom';

export const DEADLINE_OPTIONS: { value: DeadlineChoice; label: string; ms?: number }[] = [
  { value: 'none', label: 'None' },
  { value: '1h', label: 'In 1 hour', ms: 60 * MINUTE },
  { value: '4h', label: 'In 4 hours', ms: 4 * 60 * MINUTE },
  { value: '24h', label: 'In 24 hours', ms: 24 * 60 * MINUTE },
  { value: '7d', label: 'In 7 days', ms: 7 * 24 * 60 * MINUTE },
  { value: 'custom', label: 'Custom…' },
];

/** Minutes `timeZone` is ahead of UTC at instant `ms` (420 for GMT+7). */
export function tzOffsetMinutes(ms: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((wall - Math.floor(ms / 1000) * 1000) / MINUTE);
}

/** A `datetime-local` value ("2026-10-13T06:00") read as wall time in `timeZone`; NaN when malformed. */
export function zonedInputToMs(value: string, timeZone: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return NaN;
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  // Second pass: around a DST change the offset at the first guess can differ from the answer's.
  const guess = wall - tzOffsetMinutes(wall, timeZone) * MINUTE;
  return wall - tzOffsetMinutes(guess, timeZone) * MINUTE;
}

/** Epoch ms as a `datetime-local` value in `timeZone`. */
export function msToZonedInput(ms: number, timeZone: string): string {
  return new Date(ms + tzOffsetMinutes(ms, timeZone) * MINUTE).toISOString().slice(0, 16);
}

/** Whether `day` ("2026-10-12", a date in `timeZone`) can hold a custom deadline: today up to 30 days ahead. */
export function customDeadlineDayAllowed(day: string, nowMs: number, timeZone: string): boolean {
  const first = msToZonedInput(nowMs + MIN_DEADLINE_MS, timeZone).slice(0, 10);
  const last = msToZonedInput(nowMs + MAX_DEADLINE_MS, timeZone).slice(0, 10);
  return day >= first && day <= last;
}

/**
 * What a choice means at `nowMs`, as epoch ms: null for none, NaN when a
 * custom value is missing or not 1 minute to 30 days ahead. Presets count
 * from `nowMs`, so call this when Run is pressed.
 */
export function resolveDeadline(choice: DeadlineChoice, custom: string, timeZone: string, nowMs: number): number | null {
  if (choice === 'none') return null;
  const preset = DEADLINE_OPTIONS.find((o) => o.value === choice)?.ms;
  const at = preset !== undefined ? nowMs + preset : zonedInputToMs(custom, timeZone);
  const ahead = at - nowMs;
  return ahead >= MIN_DEADLINE_MS && ahead <= MAX_DEADLINE_MS ? at : NaN;
}

/**
 * "Tue 6:00 AM (GMT+7)": the deadline in `timeZone`, with its offset. Six days
 * or more from `nowMs` it also gets its date, because the weekday alone could
 * be either week: "Tue, Oct 20, 6:00 AM (GMT+7)".
 */
export function formatDeadline(ms: number, timeZone: string, nowMs = Date.now()): string {
  const at = new Date(ms).toISOString();
  const far = Math.abs(ms - nowMs) >= 6 * DAY;
  const text = formatDateTime(at, timeZone, {
    year: undefined,
    month: far ? 'short' : undefined,
    day: far ? 'numeric' : undefined,
    weekday: 'short',
  });
  return `${text} (${offsetLabel(at, timeZone)})`;
}

/** How far off the deadline is: "in 40 min", "in 18 h", "in 3 days", or "passed". */
export function deadlineIn(ms: number, nowMs: number): string {
  const mins = Math.ceil((ms - nowMs) / MINUTE);
  if (mins <= 0) return 'passed';
  if (mins < 60) return `in ${mins} min`;
  const hours = Math.round(mins / 60);
  return hours < 48 ? `in ${hours} h` : `in ${Math.round(hours / 24)} days`;
}
